import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MediaPreviewState } from 'screens/EpisodeOptionsScreen/components/EpisodeContent/MediaPreviewState';
import { useFetchEpisodeOverview } from 'client/hooks/episodes/useFetchEpisodeOverview';
import { useSaveAudioToEpisode } from 'client/hooks/episodes/useSaveAudioToEpisode';
import { useSaveVideoToEpisode } from 'client/hooks/episodes/useSaveVideoToEpisode';
import { useUploadFileMultipart } from 'client/hooks/upload/useUploadFileMultipart';
import { useCancelUpload } from 'client/hooks/upload/useCancelUpload';
import { useDeleteMediaFromEpisode } from 'client/hooks/episodes/useDeleteMediaFromEpisode';
import { useProcessFile } from 'client/hooks/upload/useProcessFile';
import { useQuickUploadFile } from 'contexts/QuickUploadFile';
import { EpisodeAudios } from 'modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';
import { UploadingState } from './UploadingState';
import { EpisodeContentLabel, UploaderStateContainer } from './styles';
import { IdleState } from './IdleState';
import { DeleteMediaConfirmationModal } from './DeleteMediaConfirmationModal';
import { CancelUploadConfirmationModal } from './CancelUploadConfirmationModal';
import { UnsupportedFileModal } from './UnsupportedFileModal';
import { getMediaPlayerTitle, isVideoFile } from './utils';

const PLAYER_INITIAL_STATE = {
  episodeAudios: [],
  containsMedia: false,
  title: '',
  totalDuration: 0,
  totalSize: 0,
  isMultiSegmentFile: false,
  isVideoEpisode: false,
  isPublished: false,
};

type PlayerInfo = {
  episodeAudios: EpisodeAudios[];
  containsMedia: boolean;
  title: string;
  totalDuration: number;
  totalSize: number;
  isMultiSegmentFile: boolean;
  isVideoEpisode: boolean;
  isPublished: boolean;
};

export const EpisodeContent = ({
  webEpisodeId,
  isVideoPodcaster,
  setHasVideo,
}: {
  webEpisodeId: string;
  isVideoPodcaster: boolean;
  setHasVideo: (hasVideo: boolean) => void;
}) => {
  const { episodeOverviewData } = useFetchEpisodeOverview({ webEpisodeId });
  const { saveAudioToEpisode } = useSaveAudioToEpisode();
  const { saveVideoToEpisode } = useSaveVideoToEpisode();
  const {
    deleteMediaFromEpisode,
    deleteMediaFromEpisodeStatus,
  } = useDeleteMediaFromEpisode();
  const { cancelUpload, cancelUploadStatus } = useCancelUpload();
  const { quickUploadFile, setQuickUploadFile } = useQuickUploadFile();

  const uploadRequest = useRef<XMLHttpRequest | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [isError, setError] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setUploading] = useState(false);
  const [uploadRequestId, setUploadRequestId] = useState('');
  const [isProcessing, setProcessing] = useState(false);
  const [playerInfo, setPlayerInfo] = useState<PlayerInfo>(
    PLAYER_INITIAL_STATE
  );
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [shouldReplaceMedia, setShouldReplaceMedia] = useState(false);
  const [
    showDeleteConfirmationModal,
    setShowDeleteConfirmationModal,
  ] = useState(false);
  const [
    showCancelUploadConfirmationModal,
    setShowCancelUploadConfirmationModal,
  ] = useState(false);
  const [showUnsupportedFileModal, setShowUnsupportedFileModal] = useState(
    false
  );

  useEffect(() => {
    if (episodeOverviewData) {
      const {
        episodeAudios,
        title,
        totalSize,
        totalDuration,
        videoPodcastingEpisode,
        isPublished,
      } = episodeOverviewData;
      setPlayerInfo({
        episodeAudios: episodeAudios || [],
        containsMedia: episodeAudios.length > 0,
        title: getMediaPlayerTitle(episodeAudios, title),
        totalDuration,
        totalSize,
        isMultiSegmentFile: episodeAudios.length > 1,
        isVideoEpisode: videoPodcastingEpisode,
        isPublished,
      });
      setHasVideo(videoPodcastingEpisode);
    }
  }, [episodeOverviewData]);

  const onError = () => {
    setError(true);
  };

  const { uploadFileMultipart } = useUploadFileMultipart();
  const { processFile } = useProcessFile({
    onProcessingSuccess: processedFile => {
      const isAudio = processedFile?.audioId && !processedFile?.videoUploadId;
      const isVideo = processedFile?.videoUploadId;

      if (isAudio) {
        saveAudioToEpisode(
          {
            webEpisodeId,
            audioId: processedFile?.audioId!,
            replaceMediaContent: shouldReplaceMedia,
            isQuickUpload: true,
          },
          {
            onError,
            onSuccess: () => {
              setShouldReplaceMedia(false);
              setProcessing(false);
            },
          }
        );
      }

      if (isVideo) {
        saveVideoToEpisode(
          {
            webEpisodeId,
            videoUploadId: processedFile?.videoUploadId!,
          },
          {
            onError,
            onSuccess: () => {
              setProcessing(false);
            },
          }
        );
      }
    },
    onError,
  });

  const onProgressUpload = (loaded: number, total: number) => {
    const percent = Math.round(Math.round((loaded / total) * 100 * 100) / 100);
    setUploadPercentage(percent);
  };

  const onUploadFile = useCallback(
    (file: File) => {
      setUploading(true);
      setUploadedFile(file);
      uploadFileMultipart(
        {
          file,
          onFetchSignedUrlSuccess: requestUuid => {
            setUploadRequestId(requestUuid);
          },
          onProgress: (xhr: XMLHttpRequest, loaded: number, total: number) => {
            onProgressUpload(loaded, total);
            uploadRequest.current = xhr;
          },
          onError,
        },
        {
          onSuccess: res => {
            setUploading(false);
            setUploadedFile(null);
            setProcessing(true);
            setHasVideo(isVideoFile(file));
            processFile({ file, webEpisodeId, ...res });
          },
        }
      );
    },
    [uploadFileMultipart, processFile, webEpisodeId]
  );

  // Upload quickUploadFile from QuickUploadFile
  // context if present
  useEffect(() => {
    if (quickUploadFile) {
      onUploadFile(quickUploadFile);
    }
  }, [onUploadFile, quickUploadFile]);

  const onCancelUpload = () => {
    if (uploadRequest.current) {
      uploadRequest.current.abort();
      uploadRequest.current = null;
    }
    cancelUpload({ requestUuid: uploadRequestId });
    setUploading(false);
    setHasVideo(false);
    setError(false);
    setQuickUploadFile(null);
    setUploadedFile(null);
    setUploadPercentage(0);
    setShowCancelUploadConfirmationModal(false);
  };

  const onRetryUpload = () => {
    setUploading(false);
    setUploadPercentage(0);
    setError(false);
    if (uploadedFile) {
      onUploadFile(uploadedFile);
    }
  };

  const onCancelProcessing = () => {
    setProcessing(false);
    setError(false);
    setUploadPercentage(0);
    setUploadedFile(null);
    setHasVideo(false);
  };

  const onDragEnter = () => setDragging(true);
  const onDragLeave = () => setDragging(false);

  const onDeleteUpload = () => {
    deleteMediaFromEpisode(
      { webEpisodeId },
      {
        onSuccess: () => setShowDeleteConfirmationModal(false),
        onError,
      }
    );
  };

  const onReplaceMedia = () => {
    setPlayerInfo(PLAYER_INITIAL_STATE);
    setShouldReplaceMedia(true);
    setHasVideo(false);
  };

  const onUploadUnsupportedFile = () => {
    setDragging(false);
    setShowUnsupportedFileModal(true);
  };

  const renderContent = () => {
    if (isUploading) {
      return (
        <UploadingState
          percentageComplete={uploadPercentage}
          isUploading={isUploading}
          onCancelUpload={() => setShowCancelUploadConfirmationModal(true)}
          onRetryUpload={onRetryUpload}
          isError={isError}
        />
      );
    }
    if (isProcessing) {
      return (
        <UploadingState
          onCancelProcessing={onCancelProcessing}
          isProcessing={isProcessing}
          percentageComplete={uploadPercentage}
          isError={isError}
        />
      );
    }
    if (playerInfo.containsMedia) {
      return (
        <MediaPreviewState
          episodeAudios={playerInfo.episodeAudios}
          isVideo={playerInfo.isVideoEpisode}
          title={playerInfo.title}
          totalSize={playerInfo.totalSize}
          onClickDelete={() => setShowDeleteConfirmationModal(true)}
          onClickReplace={onReplaceMedia}
          webEpisodeId={webEpisodeId}
          isEpisodePublished={playerInfo.isPublished}
        />
      );
    }
    return (
      <IdleState
        onUploadFile={onUploadFile}
        onUploadUnsupportedFile={onUploadUnsupportedFile}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        isVideoPodcaster={isVideoPodcaster}
      />
    );
  };

  return (
    <div>
      <EpisodeContentLabel>Episode content</EpisodeContentLabel>
      {showDeleteConfirmationModal && (
        <DeleteMediaConfirmationModal
          onDelete={onDeleteUpload}
          deleteStatus={deleteMediaFromEpisodeStatus}
          hideConfirmationModal={() => setShowDeleteConfirmationModal(false)}
        />
      )}
      {showCancelUploadConfirmationModal && (
        <CancelUploadConfirmationModal
          onCancel={onCancelUpload}
          cancelUploadStatus={cancelUploadStatus}
          hideConfirmationModal={() =>
            setShowCancelUploadConfirmationModal(false)
          }
        />
      )}
      {showUnsupportedFileModal && (
        <UnsupportedFileModal
          hideModal={() => setShowUnsupportedFileModal(false)}
        />
      )}
      <UploaderStateContainer
        isExpanded={
          (!playerInfo.containsMedia && !isUploading && !isProcessing) ||
          (playerInfo.containsMedia && playerInfo.isVideoEpisode)
        }
        containsMedia={playerInfo.containsMedia}
        isDragging={isDragging}
      >
        {renderContent()}
      </UploaderStateContainer>
    </div>
  );
};
